import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { SUPPORT_CHATS_PAGE_SIZE, chatApi } from 'api/chat'
import {
  ESupportChatTab,
  IChat,
  IChatMessage,
  IChatMessageCreatedPayload,
  ISupportChatRemovedPayload,
  ISupportChatUpdatedPayload,
  ISupportUnreadCounters
} from 'models/chat'
import { showMessage } from 'shared/utils/showMessage'
import { SupportState } from './types'

const initialState: SupportState = {
  tab: ESupportChatTab.SELLERS,
  chats: [],
  activeChat: null,
  messages: [],
  counters: { sellers: 0, buyers: 0, total: 0 },
  hasMoreChats: true,
  isChatsLoading: false,
  isMessagesLoading: false
}

// Вкладку обращения определяет тот же признак, что и на бэкенде: есть магазин — селлер
const getChatTab = (chat: IChat) =>
  (chat.support_seller_id ? ESupportChatTab.SELLERS : ESupportChatTab.BUYERS)

const upsertChat = (chats: IChat[], chat: IChat) => {
  const existingIndex = chats.findIndex((item) => item._id === chat._id)
  const nextChat = existingIndex >= 0 ? { ...chats[existingIndex], ...chat } : chat
  const rest = existingIndex >= 0
    ? chats.filter((item) => item._id !== chat._id)
    : chats

  return [nextChat, ...rest].sort((a, b) => {
    const dateA = new Date(a.last_message_at || 0).getTime()
    const dateB = new Date(b.last_message_at || 0).getTime()
    return dateB - dateA
  })
}

const mergeChats = (chats: IChat[], incoming: IChat[]) =>
  incoming.reduce((result, chat) => upsertChat(result, chat), chats)

// Сервер снимает непрочитанное разом со всех сообщений автора обращения
const markMessagesRead = (messages: IChatMessage[]) => {
  messages.forEach((message) => {
    if (message.sender_type === 'USER') message.is_read_by_support = true
  })
}

export const getSupportChats = createAsyncThunk<IChat[], ESupportChatTab>(
  'support/getChats',
  async (tab) => {
    try {
      const response = await chatApi.getSupportChats(tab)
      return response.data
    } catch (e) {
      showMessage('error', 'Ошибка', 'Не удалось загрузить обращения')
      throw e
    }
  }
)

export const getMoreSupportChats = createAsyncThunk<IChat[], void, { state: { support: SupportState } }>(
  'support/getMoreChats',
  async (_, { getState }) => {
    const { tab, chats } = getState().support
    const response = await chatApi.getSupportChats(tab, chats.length)
    return response.data
  }
)

// Пока сокет был отключён, события терялись: перечитываем первую страницу вкладки
// и открытое обращение, не сбрасывая уже догруженные страницы
export const syncSupportChats = createAsyncThunk<
  { tab: ESupportChatTab; chats: IChat[]; chatId: string | null; messages: IChatMessage[] | null },
  void,
  { state: { support: SupportState } }
>(
  'support/syncChats',
  async (_, { getState }) => {
    const { tab, activeChat } = getState().support
    const chatId = activeChat?._id ?? null
    const [chatsResponse, messagesResponse] = await Promise.all([
      chatApi.getSupportChats(tab),
      chatId ? chatApi.getSupportMessages(chatId) : null
    ])
    return {
      tab,
      chats: chatsResponse.data,
      chatId,
      messages: messagesResponse?.data ?? null
    }
  }
)

export const getSupportChat = createAsyncThunk<IChat, string>(
  'support/getChat',
  async (chatId) => {
    try {
      const response = await chatApi.getSupportChatById(chatId)
      return response.data
    } catch (e) {
      showMessage('error', 'Ошибка', 'Не удалось загрузить обращение')
      throw e
    }
  }
)

export const getSupportMessages = createAsyncThunk<IChatMessage[], string>(
  'support/getMessages',
  async (chatId) => {
    try {
      const response = await chatApi.getSupportMessages(chatId)
      return response.data
    } catch (e) {
      showMessage('error', 'Ошибка', 'Не удалось загрузить сообщения')
      throw e
    }
  }
)

export const getSupportCounters = createAsyncThunk<ISupportUnreadCounters>(
  'support/getCounters',
  async () => {
    const response = await chatApi.getSupportUnreadCount()
    return response.data
  }
)

const supportSlice = createSlice({
  name: 'support',
  initialState,
  reducers: {
    setTab(state, action: PayloadAction<ESupportChatTab>) {
      state.tab = action.payload
      state.chats = []
      state.activeChat = null
      state.messages = []
      state.hasMoreChats = true
    },
    setActiveChat(state, action: PayloadAction<IChat | null>) {
      state.activeChat = action.payload
    },
    setMessages(state, action: PayloadAction<IChatMessage[]>) {
      state.messages = action.payload
    },
    addMessage(state, action: PayloadAction<IChatMessage>) {
      if (state.messages.some((item) => item._id === action.payload._id)) return
      state.messages.push(action.payload)
    },
    applyMessageCreated(state, action: PayloadAction<IChatMessageCreatedPayload>) {
      const { chatId, chat, message } = action.payload

      if (state.activeChat?._id === chatId) {
        if (!state.messages.some((item) => item._id === message._id)) {
          state.messages.push(message)
        }
        state.activeChat = { ...state.activeChat, ...(chat || {}) }
      }
      if (chat && getChatTab(chat) === state.tab) {
        state.chats = upsertChat(state.chats, chat)
      }
    },
    applyMessageUpdated(state, action: PayloadAction<IChatMessageCreatedPayload>) {
      const { chatId, message } = action.payload
      if (state.activeChat?._id !== chatId) return

      state.messages = state.messages.map((item) => (item._id === message._id ? message : item))
    },
    applyChatUpdated(state, action: PayloadAction<ISupportChatUpdatedPayload>) {
      const { chat } = action.payload

      if (getChatTab(chat) === state.tab) {
        state.chats = upsertChat(state.chats, chat)
      } else {
        // автор обращения успел стать продавцом — обращение переехало на другую вкладку
        state.chats = state.chats.filter((item) => item._id !== chat._id)
      }
      if (state.activeChat?._id === chat._id) {
        state.activeChat = { ...state.activeChat, ...chat }
        // ответил или пометил прочитанным другой оператор
        if (chat.unread_for_support === 0) markMessagesRead(state.messages)
      }
    },
    removeChat(state, action: PayloadAction<string>) {
      state.chats = state.chats.filter((item) => item._id !== action.payload)
      if (state.activeChat?._id === action.payload) {
        state.activeChat = null
        state.messages = []
      }
    },
    markChatRead(state, action: PayloadAction<string>) {
      const chatIndex = state.chats.findIndex((item) => item._id === action.payload)
      if (chatIndex >= 0) {
        state.chats[chatIndex].unread_for_support = 0
      }
      if (state.activeChat?._id !== action.payload) return
      state.activeChat.unread_for_support = 0
      markMessagesRead(state.messages)
    },
    setCounters(state, action: PayloadAction<ISupportUnreadCounters>) {
      state.counters = action.payload
    },
    clearSupportState() {
      return initialState
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSupportChats.pending, (state) => {
        state.isChatsLoading = true
      })
      .addCase(getSupportChats.fulfilled, (state, action) => {
        if (action.meta.arg !== state.tab) return
        state.isChatsLoading = false
        // Обращения, пришедшие по сокету, пока шла загрузка, могли не попасть в ответ
        const loadedIds = new Set(action.payload.map((chat) => chat._id))
        state.chats = mergeChats(
          action.payload,
          state.chats.filter((chat) => !loadedIds.has(chat._id))
        )
        state.hasMoreChats = action.payload.length >= SUPPORT_CHATS_PAGE_SIZE
      })
      .addCase(getSupportChats.rejected, (state) => {
        state.isChatsLoading = false
      })
      .addCase(getMoreSupportChats.fulfilled, (state, action) => {
        state.chats = mergeChats(state.chats, action.payload)
        state.hasMoreChats = action.payload.length >= SUPPORT_CHATS_PAGE_SIZE
      })
      .addCase(syncSupportChats.fulfilled, (state, action) => {
        const { tab, chats, chatId, messages } = action.payload
        if (tab === state.tab) {
          state.chats = mergeChats(state.chats, chats)
        }
        if (messages && chatId === state.activeChat?._id) {
          state.messages = messages
        }
      })
      .addCase(getSupportChat.fulfilled, (state, action) => {
        state.activeChat = action.payload
        if (getChatTab(action.payload) === state.tab) {
          state.chats = upsertChat(state.chats, action.payload)
        }
      })
      .addCase(getSupportMessages.pending, (state) => {
        state.isMessagesLoading = true
      })
      .addCase(getSupportMessages.fulfilled, (state, action) => {
        state.isMessagesLoading = false
        state.messages = action.payload
      })
      .addCase(getSupportMessages.rejected, (state) => {
        state.isMessagesLoading = false
      })
      .addCase(getSupportCounters.fulfilled, (state, action) => {
        state.counters = action.payload
      })
      .addCase('app/logout', () => initialState)
  }
})

export const supportActions = supportSlice.actions
export default supportSlice.reducer

// Открытое у оператора обращение гостя не пропадает, а сменяется тем, в которое влилось
export const applySupportChatRemoved = createAsyncThunk<
  void,
  ISupportChatRemovedPayload,
  { state: { support: SupportState } }
>(
  'support/chatRemoved',
  async ({ chatId, mergedInto }, { dispatch, getState }) => {
    const isActive = getState().support.activeChat?._id === chatId
    dispatch(supportActions.removeChat(chatId))
    if (!isActive || !mergedInto) return

    await dispatch(getSupportChat(mergedInto))
    await dispatch(getSupportMessages(mergedInto))
  }
)
